import { CONFIG } from './config';
import { CountingRound, generateStarLayout } from './counting';
import type { Narration } from './narration';
import type { VoiceId } from './voices';

export const COUNTING_CONFIG = {
  idleHintMs: 8000,
  rocketIntroMs: 1200,
  starPopMs: 700,
  numeralMs: 1100,
  duplicateWobbleMs: 400,
  resizeDebounceMs: 150,
} as const;

export interface CountingActivityRefs {
  screen: HTMLElement;
  starsLayer: HTMLElement;
  numeral: HTMLElement;
  total: HTMLElement;
  rocket: HTMLElement;
  caption: HTMLElement;
  hud: HTMLElement;
  muteButton: HTMLButtonElement;
  muteIcon: HTMLImageElement;
  hearButton: HTMLButtonElement;
  completeOverlay: HTMLElement;
  replayButton: HTMLButtonElement;
  backButton: HTMLButtonElement;
  level1MuteIcon?: HTMLImageElement;
}

export type CountingNarration = Pick<
  Narration,
  'play' | 'pickCountingVoice' | 'stop' | 'setMuted'
> & {
  readonly muted_: boolean;
};

export interface CountingActivityOptions {
  onBack?: () => void;
}

export class CountingActivity {
  private round: CountingRound;
  private voice: VoiceId = 'computer';
  private starsMap = new Map<number, HTMLButtonElement>();
  private idleTimer: number | null = null;
  private numeralTimer: number | null = null;
  private wobbleTimers = new Map<number, number>();
  private popTimers = new Map<number, number>();
  private resizeTimer: number | null = null;
  private rocketTimer: number | null = null;
  private generation = 0;
  private completionStarted = false;
  private windowListeners: Array<() => void> = [];
  private reducedMotion = false;

  constructor(
    private refs: CountingActivityRefs,
    private narration: CountingNarration,
    private options?: CountingActivityOptions,
  ) {
    this.round = new CountingRound();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.attachHud();
    this.attachWindowListeners();
    this.syncMute(this.narration.muted_);
  }

  async begin(): Promise<void> {
    this.generation += 1;
    this.clearAllTimers();
    this.completionStarted = false;
    this.round.reset();
    this.voice = this.narration.pickCountingVoice();
    this.refs.screen.dataset.voice = this.voice;
    this.refs.screen.dataset.count = '0';
    delete this.refs.screen.dataset.complete;
    this.refs.total.textContent = '0 stars counted';
    this.refs.completeOverlay.hidden = true;
    this.renderStars();
    this.triggerRocketIntro();
    this.startIdleHintTimer();
    await this.narration.play('count-intro');
  }

  async replay(): Promise<void> {
    this.generation += 1;
    this.clearAllTimers();
    this.completionStarted = false;
    this.round.reset();
    this.voice = this.narration.pickCountingVoice();
    this.refs.screen.dataset.voice = this.voice;
    this.refs.screen.dataset.count = '0';
    delete this.refs.screen.dataset.complete;
    this.refs.total.textContent = '0 stars counted';
    this.refs.completeOverlay.hidden = true;
    this.renderStars();
    this.startIdleHintTimer();
    await this.narration.play('count-intro');
  }

  suspend(): void {
    this.generation += 1;
    this.clearAllTimers();
    this.completionStarted = false;
    this.narration.stop();
    this.refs.completeOverlay.hidden = true;
    this.refs.screen.hidden = true;
  }

  dispose(): void {
    this.suspend();
    for (const remove of this.windowListeners) {
      remove();
    }
    this.windowListeners = [];
  }

  private clearAllTimers(): void {
    this.clearIdleHintTimer();

    if (this.numeralTimer !== null) {
      clearTimeout(this.numeralTimer);
      this.numeralTimer = null;
    }
    this.refs.numeral.classList.remove('is-visible');

    if (this.resizeTimer !== null) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }

    if (this.rocketTimer !== null) {
      clearTimeout(this.rocketTimer);
      this.rocketTimer = null;
    }
    this.refs.rocket.classList.remove('is-flying');

    for (const timer of this.wobbleTimers.values()) {
      clearTimeout(timer);
    }
    this.wobbleTimers.clear();

    for (const timer of this.popTimers.values()) {
      clearTimeout(timer);
    }
    this.popTimers.clear();

    for (const btn of this.starsMap.values()) {
      btn.classList.remove('is-wobble', 'is-popping', 'is-celebrating');
    }
  }

  private attachHud(): void {
    this.refs.muteButton.addEventListener('click', () => {
      const next = !this.narration.muted_;
      this.narration.setMuted(next);
      try {
        localStorage.setItem(CONFIG.muteStorageKey, next ? '1' : '0');
      } catch {
        /* localStorage may be unavailable in private browsing mode */
      }
      this.syncMute(next);
    });

    this.refs.hearButton.addEventListener('click', () => {
      this.startIdleHintTimer();
      void this.narration.play('count-tap-stars');
    });

    this.refs.replayButton.addEventListener('click', () => {
      void this.replay();
    });

    this.refs.backButton.addEventListener('click', () => {
      this.suspend();
      this.options?.onBack?.();
    });
  }

  private syncMute(muted: boolean): void {
    const src = muted ? '/art/icon-speaker-off.svg' : '/art/icon-speaker-on.svg';
    this.refs.muteIcon.src = src;
    const l1 = this.refs.level1MuteIcon ?? (document.getElementById('mute-icon') as HTMLImageElement | null);
    if (l1) {
      l1.src = src;
    }
  }

  private renderStars(): void {
    this.refs.starsLayer.innerHTML = '';
    this.starsMap.clear();
    const slots = generateStarLayout(window.innerWidth, window.innerHeight);
    for (const slot of slots) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'count-star';
      btn.dataset.starId = String(slot.id);
      btn.dataset.counted = 'false';
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', 'Star');
      btn.style.left = `${slot.cx - slot.size / 2}px`;
      btn.style.top = `${slot.cy - slot.size / 2}px`;
      btn.style.width = `${slot.size}px`;
      btn.style.height = `${slot.size}px`;
      btn.style.touchAction = 'manipulation';

      const img = document.createElement('img');
      img.src = '/art/star.svg';
      img.alt = '';
      img.draggable = false;
      btn.appendChild(img);

      btn.addEventListener('click', () => {
        this.onStarClick(slot.id, btn);
      });

      this.refs.starsLayer.appendChild(btn);
      this.starsMap.set(slot.id, btn);
    }
  }

  private onStarClick(id: number, btn: HTMLButtonElement): void {
    const result = this.round.tap(id);
    if (result.kind === 'counted') {
      this.clearNudge();

      btn.dataset.counted = 'true';
      btn.setAttribute('aria-pressed', 'true');
      btn.classList.add('is-counted');
      const img = btn.querySelector('img');
      if (img) {
        img.src = '/art/star-counted.svg';
      }

      const existingPop = this.popTimers.get(id);
      if (existingPop !== undefined) {
        clearTimeout(existingPop);
      }
      btn.classList.remove('is-popping');
      void btn.offsetWidth;
      btn.classList.add('is-popping');
      const gen = this.generation;
      const popTimer = window.setTimeout(() => {
        if (this.generation === gen) {
          btn.classList.remove('is-popping');
        }
        this.popTimers.delete(id);
      }, COUNTING_CONFIG.starPopMs);
      this.popTimers.set(id, popTimer);

      this.refs.screen.dataset.count = String(result.count);
      this.refs.total.textContent = `${result.count} star${result.count === 1 ? '' : 's'} counted`;

      this.showNumeral(result.count, btn);

      const countPlayPromise = this.narration.play('count-' + result.count, { voice: this.voice });

      if (!result.complete) {
        this.startIdleHintTimer();
      } else {
        this.clearIdleHintTimer();
        void this.handleCompletion(this.round.epoch, countPlayPromise);
      }
    } else {
      if (!this.reducedMotion) {
        const existingWobble = this.wobbleTimers.get(id);
        if (existingWobble !== undefined) {
          clearTimeout(existingWobble);
        }
        btn.classList.remove('is-wobble');
        void btn.offsetWidth;
        btn.classList.add('is-wobble');
        const gen = this.generation;
        const wobbleTimer = window.setTimeout(() => {
          if (this.generation === gen) {
            btn.classList.remove('is-wobble');
          }
          this.wobbleTimers.delete(id);
        }, COUNTING_CONFIG.duplicateWobbleMs);
        this.wobbleTimers.set(id, wobbleTimer);
      }
    }
  }

  private async handleCompletion(epoch: number, tenthPlay: Promise<void>): Promise<void> {
    if (this.completionStarted) return;
    this.completionStarted = true;
    const gen = this.generation;
    this.clearIdleHintTimer();

    await tenthPlay;
    if (this.round.epoch !== epoch || this.generation !== gen) return;

    for (const star of this.starsMap.values()) {
      star.classList.add('is-celebrating');
    }

    await this.narration.play('count-all-ten');
    if (this.round.epoch !== epoch || this.generation !== gen) return;

    this.refs.completeOverlay.hidden = false;
    this.refs.screen.dataset.complete = 'true';
  }

  private showNumeral(count: number, starBtn: HTMLButtonElement): void {
    if (this.numeralTimer !== null) {
      clearTimeout(this.numeralTimer);
      this.numeralTimer = null;
    }
    const numeral = this.refs.numeral;
    numeral.textContent = String(count);

    const left = parseFloat(starBtn.style.left) + parseFloat(starBtn.style.width) / 2;
    const top = parseFloat(starBtn.style.top) + parseFloat(starBtn.style.height) / 2;
    numeral.style.left = `${left}px`;
    numeral.style.top = `${top}px`;

    numeral.classList.remove('is-visible');
    void numeral.offsetWidth;
    numeral.classList.add('is-visible');

    const gen = this.generation;
    this.numeralTimer = window.setTimeout(() => {
      if (this.generation === gen) {
        numeral.classList.remove('is-visible');
        this.numeralTimer = null;
      }
    }, COUNTING_CONFIG.numeralMs);
  }

  private startIdleHintTimer(): void {
    this.clearIdleHintTimer();
    const gen = this.generation;
    this.idleTimer = window.setTimeout(() => {
      this.idleTimer = null;
      if (this.generation !== gen || this.round.count >= 10) return;
      this.nudgeUncountedStar();
      void this.narration.play('count-tap-stars');
    }, COUNTING_CONFIG.idleHintMs);
  }

  private clearIdleHintTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    this.clearNudge();
  }

  private nudgeUncountedStar(): void {
    this.clearNudge();
    for (const [id, btn] of this.starsMap.entries()) {
      if (!this.round.isCounted(id)) {
        btn.classList.add('is-nudge');
        break;
      }
    }
  }

  private clearNudge(): void {
    for (const btn of this.starsMap.values()) {
      btn.classList.remove('is-nudge');
    }
  }

  private triggerRocketIntro(): void {
    if (this.rocketTimer !== null) {
      clearTimeout(this.rocketTimer);
      this.rocketTimer = null;
    }
    const rocket = this.refs.rocket;
    rocket.classList.remove('is-flying');
    if (this.reducedMotion) return;
    void rocket.offsetWidth;
    rocket.classList.add('is-flying');
    const gen = this.generation;
    this.rocketTimer = window.setTimeout(() => {
      if (this.generation === gen) {
        rocket.classList.remove('is-flying');
      }
      this.rocketTimer = null;
    }, COUNTING_CONFIG.rocketIntroMs);
  }

  private attachWindowListeners(): void {
    const onResizeOrRotate = () => {
      if (this.resizeTimer !== null) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = window.setTimeout(() => {
        this.repositionStars();
        this.resizeTimer = null;
      }, COUNTING_CONFIG.resizeDebounceMs);
    };

    window.addEventListener('resize', onResizeOrRotate);
    window.addEventListener('orientationchange', onResizeOrRotate);

    this.windowListeners.push(() => {
      window.removeEventListener('resize', onResizeOrRotate);
      window.removeEventListener('orientationchange', onResizeOrRotate);
    });
  }

  private repositionStars(): void {
    if (this.refs.screen.hidden || this.starsMap.size === 0) return;
    const slots = generateStarLayout(window.innerWidth, window.innerHeight);
    for (const slot of slots) {
      const btn = this.starsMap.get(slot.id);
      if (btn) {
        btn.style.left = `${slot.cx - slot.size / 2}px`;
        btn.style.top = `${slot.cy - slot.size / 2}px`;
        btn.style.width = `${slot.size}px`;
        btn.style.height = `${slot.size}px`;
      }
    }
  }
}
